import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, CheckCircle, ArrowRight, Award, Zap, Eye, Bell, BarChart2, TrendingUp, AlertCircle } from 'lucide-react';

export default function MonitoringObservability() {
  const benefits = [
    {
      icon: <Eye className="w-6 h-6" />,
      title: "Complete Visibility",
      description: "Real-time insights into infrastructure, applications, and business metrics"
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: "Proactive Alerting",
      description: "Intelligent alerts preventing issues before they impact users"
    },
    {
      icon: <BarChart2 className="w-6 h-6" />,
      title: "Custom Dashboards",
      description: "Beautiful, actionable dashboards for teams and executives"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "15 Min MTTR",
      description: "Rapid incident detection and resolution reducing downtime"
    }
  ];

  const monitoringLayers = [
    {
      layer: "Infrastructure Monitoring",
      description: "Complete visibility into servers, containers, and cloud resources",
      metrics: [
        "CPU, Memory, Disk utilization",
        "Network traffic and latency",
        "Container and pod health",
        "Cloud service metrics"
      ],
      tools: ["Prometheus", "CloudWatch", "Azure Monitor", "Node Exporter"]
    },
    {
      layer: "Application Monitoring",
      description: "Application performance and user experience tracking",
      metrics: [
        "Request rates and latency",
        "Error rates and types",
        "Database query performance",
        "API endpoint metrics"
      ],
      tools: ["New Relic", "Datadog APM", "Application Insights", "Dynatrace"]
    },
    {
      layer: "Log Management",
      description: "Centralized logging for debugging and compliance",
      metrics: [
        "Application logs",
        "System logs",
        "Security audit logs",
        "Access logs"
      ],
      tools: ["ELK Stack", "Loki", "CloudWatch Logs", "Splunk"]
    },
    {
      layer: "Distributed Tracing",
      description: "End-to-end request tracking across microservices",
      metrics: [
        "Service dependencies",
        "Request flow visualization",
        "Performance bottlenecks",
        "Error propagation"
      ],
      tools: ["Jaeger", "Zipkin", "AWS X-Ray", "Tempo"]
    }
  ];

  const dashboardTypes = [
    {
      type: "Executive Dashboard",
      purpose: "High-level business and infrastructure KPIs",
      metrics: ["Uptime %", "Cost trends", "User growth", "Critical incidents"]
    },
    {
      type: "DevOps Dashboard",
      purpose: "Operational metrics for engineering teams",
      metrics: ["Deployment frequency", "Change failure rate", "MTTR", "Resource utilization"]
    },
    {
      type: "Application Dashboard",
      purpose: "Real-time application performance monitoring",
      metrics: ["Response time", "Error rates", "Throughput", "Database performance"]
    },
    {
      type: "Security Dashboard",
      purpose: "Security events and compliance monitoring",
      metrics: ["Failed logins", "Unauthorized access", "Vulnerability scans", "Compliance status"]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&auto=format&fit=crop&q=80"
            alt="Monitoring & Observability"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/95 via-indigo-900/90 to-purple-900/95"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm text-white mb-6">
              <Activity className="w-4 h-4 text-cyan-400" />
              <span>Real-Time Observability</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Monitoring & Observability
            </h1>

            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              Gain complete visibility into your infrastructure with real-time monitoring,
              logging, and alerting. Proactive incident detection and resolution.
            </p>

            <div className="flex flex-wrap gap-4">
              <a href="https://calendly.com/aneeshthedevops/hello-there-intro-call" target="_blank" rel="noopener noreferrer"
                className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 px-8 py-4 rounded-full font-bold transition-all hover:scale-105 shadow-2xl inline-flex items-center gap-2">
                Schedule Consultation <ArrowRight className="w-5 h-5" />
              </a>
              <Link to="/#case-studies"
                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold transition-all inline-flex items-center gap-2">
                View Case Studies
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Observability Matters</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Know what's happening in your systems before your users notice issues
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center text-white mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Monitoring Layers */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Complete Observability Stack</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Four pillars of modern observability covering infrastructure to applications
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {monitoringLayers.map((layer, idx) => (
              <div key={idx} className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-lg mb-4">
                  {idx + 1}
                </div>
                <h3 className="text-2xl font-bold mb-3 text-purple-600">{layer.layer}</h3>
                <p className="text-gray-700 mb-6">{layer.description}</p>

                <div className="mb-6">
                  <h4 className="font-bold mb-3 text-sm text-gray-900">Key Metrics:</h4>
                  <ul className="space-y-2">
                    {layer.metrics.map((metric, metricIdx) => (
                      <li key={metricIdx} className="flex items-center gap-2 text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                        {metric}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold mb-2 text-sm text-gray-900">Tools:</h4>
                  <div className="flex flex-wrap gap-2">
                    {layer.tools.map((tool, toolIdx) => (
                      <span key={toolIdx} className="bg-white text-purple-700 px-3 py-1 rounded-lg text-xs font-semibold">
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dashboard Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Custom Dashboards</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tailored visualizations for every stakeholder
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {dashboardTypes.map((dashboard, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-lg font-bold mb-2 text-purple-600">{dashboard.type}</h3>
                <p className="text-sm text-gray-600 mb-4">{dashboard.purpose}</p>
                <ul className="space-y-2">
                  {dashboard.metrics.map((metric, metricIdx) => (
                    <li key={metricIdx} className="flex items-center gap-2 text-xs text-gray-700">
                      <div className="w-1.5 h-1.5 bg-purple-600 rounded-full"></div>
                      {metric}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Service Description */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Production-Grade Observability
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                I've implemented monitoring solutions for 200+ production systems, reducing Mean Time to Resolution (MTTR) to under 15 minutes. From Prometheus and Grafana to ELK and Datadog, I deliver comprehensive observability.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Prometheus & Grafana</h4>
                    <p className="text-gray-600">Custom metrics, alerts, and beautiful dashboards for Kubernetes and cloud infrastructure</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Centralized Logging</h4>
                    <p className="text-gray-600">ELK Stack, Loki, and CloudWatch Logs for debugging and compliance</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Intelligent Alerting</h4>
                    <p className="text-gray-600">PagerDuty, Opsgenie integration with ML-powered anomaly detection</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">APM & Tracing</h4>
                    <p className="text-gray-600">Distributed tracing for microservices with Jaeger and AWS X-Ray</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop&q=80"
                alt="Monitoring Dashboard"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-2xl max-w-xs">
                <div className="flex items-center gap-3 mb-2">
                  <AlertCircle className="w-8 h-8 text-purple-600" />
                  <div className="text-3xl font-bold text-purple-600">15min</div>
                </div>
                <div className="text-sm text-gray-600">Average MTTR</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Award className="w-16 h-16 mx-auto mb-4" />
            <h2 className="text-4xl font-bold mb-4">Success Story</h2>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/20">
            <h3 className="text-3xl font-bold mb-4">E-Commerce Platform Observability</h3>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h4 className="font-bold text-xl mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  Challenge
                </h4>
                <p className="text-purple-100">No visibility into microservices, 2-hour MTTR, frequent production incidents affecting revenue</p>
              </div>
              <div>
                <h4 className="font-bold text-xl mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  Solution
                </h4>
                <p className="text-purple-100">Implemented Prometheus, Grafana, ELK Stack, and Jaeger with custom dashboards and intelligent alerting</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { metric: "MTTR", value: "15 min", improvement: "-87.5%" },
                { metric: "Incident Detection", value: "Real-time", improvement: "Proactive alerts" },
                { metric: "Dashboard Views", value: "12", improvement: "For all stakeholders" },
                { metric: "Downtime", value: "-95%", improvement: "Prevented outages" }
              ].map((result, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-sm text-purple-200 mb-2">{result.metric}</div>
                  <div className="text-3xl font-bold mb-1">{result.value}</div>
                  <div className="text-xs text-green-300">{result.improvement}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready for Complete Visibility?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Let's build a comprehensive observability solution for your infrastructure
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://calendly.com/aneeshthedevops/hello-there-intro-call" target="_blank" rel="noopener noreferrer"
              className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 px-10 py-5 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-2xl inline-flex items-center gap-2">
              Schedule Free Consultation <ArrowRight className="w-6 h-6" />
            </a>
            <Link to="/"
              className="bg-white/10 backdrop-blur-sm border-2 border-white/30 hover:bg-white/20 text-white px-10 py-5 rounded-full font-bold text-lg transition-all inline-flex items-center gap-2">
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
